import { useState, useCallback } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { BlockEditModal } from "../BlockEditModal";

export function CvEntryNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const period = (node.attrs.period as string) || "";
  const role = (node.attrs.role as string) || "";
  const org = (node.attrs.org as string) || "";
  const location = (node.attrs.location as string) || "";
  const description = (node.attrs.description as string) || "";

  const [isOpen, setIsOpen] = useState(false);
  const [draftPeriod, setDraftPeriod] = useState(period);
  const [draftRole, setDraftRole] = useState(role);
  const [draftOrg, setDraftOrg] = useState(org);
  const [draftLocation, setDraftLocation] = useState(location);
  const [draftDescription, setDraftDescription] = useState(description);

  const handleOpen = useCallback(() => {
    setDraftPeriod(period); setDraftRole(role); setDraftOrg(org);
    setDraftLocation(location); setDraftDescription(description);
    setIsOpen(true);
  }, [period, role, org, location, description]);

  const handleSave = useCallback(() => {
    updateAttributes({
      period: draftPeriod, role: draftRole, org: draftOrg,
      location: draftLocation, description: draftDescription,
    });
    setIsOpen(false);
  }, [draftPeriod, draftRole, draftOrg, draftLocation, draftDescription, updateAttributes]);

  const isEmpty = !role && !org && !period && !description;

  return (
    <NodeViewWrapper data-type="cv-entry-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div onClick={handleOpen} style={{
        padding: "10px 12px",
        margin: "6px 0",
        borderRadius: 4,
        border: "1px solid transparent",
        cursor: "pointer",
        transition: "background 120ms",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--lilia-surface-hover, #f5f5f5)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      title="Click to edit CV entry">
        {isEmpty ? (
          <span style={{ color: "var(--lilia-text-muted, #999)", fontStyle: "italic" }}>
            Click to add a CV entry (role, organisation, period…)
          </span>
        ) : (
          <>
            <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 700, color: "var(--lilia-text, #111)" }}>{role}</span>
                {org && <span style={{ fontWeight: 700, color: "var(--lilia-text, #111)" }}>{role && ", "}{org}</span>}
                {location && <span style={{ color: "var(--lilia-text-muted, #555)" }}> — {location}</span>}
              </div>
              {period && <span style={{ fontVariantNumeric: "tabular-nums", fontSize: 13, color: "var(--lilia-text-muted, #555)", whiteSpace: "nowrap" }}>{period}</span>}
            </div>
            {description && <div style={{ marginTop: 4, color: "var(--lilia-text, #333)", fontSize: 14, lineHeight: 1.5 }}>{description}</div>}
          </>
        )}
      </div>

      <BlockEditModal
        sourceNode={node}
        title="Edit CV Entry"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={560}
      >
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <Field label="Role / Title" value={draftRole} onChange={setDraftRole} placeholder="Senior Engineer" autoFocus />
            <Field label="Period" value={draftPeriod} onChange={setDraftPeriod} placeholder="2023 – Present" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
            <Field label="Organisation" value={draftOrg} onChange={setDraftOrg} placeholder="Company / University" />
            <Field label="Location" value={draftLocation} onChange={setDraftLocation} placeholder="City" />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Description</label>
            <textarea
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              placeholder="What you did, achievements, technologies…"
              rows={4}
              style={{
                width: "100%", padding: "8px 10px", fontSize: 13,
                border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4,
                resize: "vertical", lineHeight: 1.5,
              }}
            />
          </div>
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}

function Field({ label, value, onChange, placeholder, autoFocus = false }: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; autoFocus?: boolean;
}) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, marginBottom: 4 }}>{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} autoFocus={autoFocus}
        style={{ width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid var(--lilia-border, #ddd)", borderRadius: 4 }} />
    </div>
  );
}
